import { Image as ImageIcon } from "lucide-react";
import { Field, Icon } from "@cntrl/ui";
import type {
  SharedShowDetail,
  SharedLineupSlot,
  SharedRunOfShowItem,
} from "../../app/hooks/useShowShares.js";
import { useSharedFlyerUrl } from "../../app/hooks/useMedia.js";
import { AcMark } from "./AcMark.js";
import { slotRoleLabel, sharedRunOfShowLabel, sharedShowFormatTime } from "./sharedShowFormat.js";
import styles from "./sharedShowSections.module.scss";

/**
 * The READ-ONLY section views for a shared show (ART-165): basics / flyer / lineup / run of show — the
 * INVITER-owned sections a guest sees but cannot edit.
 *
 * These render the SAME shape as the owner's editor, DISABLED — Ryan's ART-165 ruling: the guest editor is
 * "THE same modal … with the fields they don't have access to edit rendering as disabled, not rendering
 * differently altogether". So Basics is the editor's own `Field` grid with every input disabled, not a bespoke
 * facts list. The flyer shows the real artwork (the guest-reachable shared-flyer route), and the lineup lights
 * the AC mark per act, so the two things that were missing for a guest (the poster, the mark) are present here
 * too. Rendered inside the guest editor's inviter-section slots (the read-only view modal is `ShowPreview` in
 * guest mode, which renders the flyer + lineup itself) — one implementation, so the surfaces cannot disagree.
 */

/** Basics — the editor's own Date/type/venue/location/capacity/ages/tickets grid, every field DISABLED. Only
 *  the fields the matrix carries appear (no promoter/tour — the guest is never handed those). */
export function SharedBasicsView({ detail }: { detail: SharedShowDetail }) {
  const isFestival = detail.kind === "festival";
  return (
    <section className={styles.section} aria-labelledby="shared-basics-heading">
      <p id="shared-basics-heading" className={styles.sectionLabel}>Basics</p>
      <div className={styles.grid}>
        <Field label="Date" type="date" value={detail.date ?? ""} disabled readOnly />
        <Field label="Show type" value={isFestival ? "Festival" : "Show"} disabled readOnly />
        <Field label={isFestival ? "Festival" : "Venue"} value={detail.venueName ?? ""} disabled readOnly />
        <Field label="City" value={detail.city ?? ""} disabled readOnly />
        <Field label="State" value={detail.state ?? ""} disabled readOnly />
        <Field label="Country" value={detail.country ?? ""} disabled readOnly />
        <Field label="Capacity" value={detail.capacity != null ? String(detail.capacity) : ""} disabled readOnly />
        <Field label="Age restriction" value={detail.ageRestriction ?? ""} disabled readOnly />
        <Field label="Ticket link" value={detail.ticketUrl ?? ""} disabled readOnly />
      </div>
    </section>
  );
}

/** Flyer — the REAL artwork (ART-165). The host's flyer bytes live in the host's tenant, so a guest fetches
 *  them from the guest-reachable shared-flyer route (`useSharedFlyerUrl`), which serves them cross-tenant after
 *  the same authorization the matrix read takes. No flyer, or bytes not resolved: the designed empty/loading
 *  state, never a broken image. */
export function SharedFlyerView({ detail }: { detail: SharedShowDetail }) {
  const hasFlyer = detail.flyerMediaId != null;
  const art = useSharedFlyerUrl(detail.showId, hasFlyer, "sm");
  return (
    <section className={styles.section} aria-labelledby="shared-flyer-heading">
      <p id="shared-flyer-heading" className={styles.sectionLabel}>Flyer</p>
      {hasFlyer && art.url ? (
        <img
          className={styles.flyerArt}
          src={art.url}
          alt={`Flyer for ${detail.venueName || "this show"}`}
        />
      ) : hasFlyer && art.loading ? (
        // Hold the well rather than flashing the empty state over a flyer that exists but is still resolving.
        <div className={styles.flyerLoading} aria-label="Loading flyer" />
      ) : (
        <div className={styles.flyerEmpty}>
          <Icon icon={ImageIcon} size={16} />
          <span>No flyer yet</span>
        </div>
      )}
    </section>
  );
}

/** Lineup — read-only (the guest cannot edit another band's lineup), each act carrying the AC mark when it is
 *  a band on Artist CNTRL (`friendBandId`) and its set time from the host's run of show. */
export function SharedLineupView({ detail }: { detail: SharedShowDetail }) {
  const lineup = detail.lineup ?? [];
  const runOfShow = detail.runOfShow ?? [];
  return (
    <section className={styles.section} aria-labelledby="shared-lineup-heading">
      <p id="shared-lineup-heading" className={styles.sectionLabel}>Lineup</p>
      {lineup.length === 0 ? (
        <p className={styles.sectionEmpty}>No lineup yet.</p>
      ) : (
        <ol className={styles.lineup}>
          {[...lineup]
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((slot: SharedLineupSlot) => {
              const set = runOfShow.find((i) => i.section === "stage" && i.slotId === slot.slotId);
              return (
                <li key={slot.slotId ?? slot.position} className={styles.lineupRow}>
                  <span className={styles.slotRole}>
                    {slotRoleLabel(slot.slotRole) ?? "—"}
                  </span>
                  <span className={styles.slotArtist}>
                    {slot.artistName || "TBA"}
                    {slot.friendBandId && (
                      <AcMark title={`${slot.artistName || "This act"} is on Artist CNTRL`} />
                    )}
                  </span>
                  {set?.startsAt && (
                    <span className={styles.slotTime}>{sharedShowFormatTime(set.startsAt)}</span>
                  )}
                </li>
              );
            })}
        </ol>
      )}
    </section>
  );
}

/** Run of show — READ-ONLY for a guest (the inviter owns it), rendered as the same per-act grid the editor uses. */
export function SharedRunOfShowView({ detail }: { detail: SharedShowDetail }) {
  const lineup = detail.lineup ?? [];
  const runOfShow = detail.runOfShow ?? [];
  const unattributed = runOfShow.filter(
    (i: SharedRunOfShowItem) => !lineup.some((sl: SharedLineupSlot) => sl.slotId === i.slotId));

  return (
    <section className={styles.section} aria-labelledby="shared-ros-heading">
      <p id="shared-ros-heading" className={styles.sectionLabel}>Run of show</p>
      {runOfShow.length === 0 ? (
        <p className={styles.sectionEmpty}>No run of show yet.</p>
      ) : (
        <>
          <div className={styles.rosGrid} role="table" aria-label="Run of show by act">
            <div className={styles.rosGridHead} role="row">
              <span role="columnheader">Act</span>
              <span role="columnheader">Load in</span>
              <span role="columnheader">Soundcheck</span>
              <span role="columnheader">Set</span>
              <span role="columnheader">Load out</span>
            </div>
            {lineup.map((slot: SharedLineupSlot) => {
              const mine = runOfShow.filter((i: SharedRunOfShowItem) => i.slotId === slot.slotId);
              const at = (section: string) => mine.find((i) => i.section === section);
              const span = (section: string) => {
                const row = at(section);
                if (!row?.startsAt) return "—";
                const start = sharedShowFormatTime(row.startsAt) || "—";
                return row.durationMin ? `${start} · ${row.durationMin} min` : start;
              };
              return (
                <div key={slot.slotId} className={styles.rosGridRow} role="row">
                  <span className={styles.rosAct}>{slot.artistName || "TBA"}</span>
                  <span>{sharedShowFormatTime(at("load_in")?.startsAt) || "—"}</span>
                  <span>{span("soundcheck")}</span>
                  <span>{span("stage")}</span>
                  <span>{sharedShowFormatTime(at("load_out")?.startsAt) || "—"}</span>
                </div>
              );
            })}
          </div>
          {unattributed.length > 0 && (
            <ul className={styles.ros}>
              {unattributed.map((item: SharedRunOfShowItem) => (
                <li key={item.itemId} className={styles.rosRow}>
                  <span className={styles.rosTime}>{sharedShowFormatTime(item.startsAt) || "—"}</span>
                  <span className={styles.rosMain}>
                    <span className={styles.rosLabel}>{sharedRunOfShowLabel(item)}</span>
                    {item.notes && <span className={styles.rosNotes}>{item.notes}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
